import { HttpClient } from '@angular/common/http';
import { Injectable, Injector, NgZone } from '@angular/core';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';
import { ToasterService } from './toaster.service';
import { Analytics } from './analytics/analytics.service';
import { EventType } from '../enums/meta.enum';
import { AppConstant } from '../constant/app-constant';
import { UserCountryService } from '@app/public/shared/services/user-country.service';

declare let Razorpay: any;

export interface RazorpayOrderResponse {
  razorpayOrderId: string;
  razorpayKeyId: string;
  amount: number;
  amountInRupees: number;
  currency: string;
  orderId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
}

export interface RazorpayPaymentResult {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

@Injectable({
  providedIn: 'root',
})
export class RazorpayService {
  readonly baseUrl: string = environment.apiUrl;
  private scriptLoaded: Promise<boolean> | null = null;
  private scriptElement: HTMLScriptElement | null = null;

  constructor(
    private readonly toaster: ToasterService,
    private readonly http: HttpClient,
    private readonly injector: Injector,
    private readonly userCountryService: UserCountryService,
    private readonly ngZone: NgZone,
  ) {}

  /**
   * Lazily loads the Razorpay checkout script only when needed.
   * Caches the promise so the script is loaded at most once.
   */
  private loadScript(): Promise<boolean> {
    if (this.scriptLoaded) {
      return this.scriptLoaded;
    }

    this.scriptLoaded = new Promise<boolean>((resolve) => {
      if ((globalThis as any).Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        this.scriptElement = script;
        resolve(true);
      };
      script.onerror = () => {
        this.scriptLoaded = null; // allow retry on failure
        resolve(false);
      };
      document.body.appendChild(script);
    });

    return this.scriptLoaded;
  }

  /**
   * Removes the Razorpay script from the DOM and cleans up the global reference.
   * This stops all background network requests made by the SDK.
   */
  private unloadScript(): void {
    this.scriptElement?.remove();
    this.scriptElement = null;
    // Remove all Razorpay-injected iframes and elements
    document
      .querySelectorAll('iframe[src*="razorpay"], .razorpay-container')
      .forEach((el) => el.remove());
    delete (globalThis as any).Razorpay;
    this.scriptLoaded = null;
  }

  private get analytics(): Analytics {
    return this.injector.get(Analytics);
  }

  /**
   * Creates a Razorpay order for wallet recharge (add money)
   */
  createAddMoneyOrder(amount: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/wallet/razorpay/addMoney`, {
      amount,
      countryCode: this.userCountryService._countryCode(),
      deviceType: AppConstant.DEVICE_TYPE,
    });
  }

  /**
   * Creates a Razorpay order for content purchase (Film/WebSeries)
   */
  createContentOrder(
    contentId: string,
    params: {
      contentType: string;
      amount: number;
      walletAmount: number;
      rewardAmount: number;
      seasonId?: string;
      couponCode?: string | null;
      couponType?: string | null;
    },
  ): Observable<any> {
    const payload: any = {
      contentType: params.contentType,
      amount: params.amount,
      walletAmount: params.walletAmount,
      rewardAmount: params.rewardAmount,
      countryCode: this.userCountryService._countryCode(),
      deviceType: AppConstant.DEVICE_TYPE,
    };

    if (params.seasonId) {
      payload.seasonId = params.seasonId;
    }
    if (params.couponCode) {
      payload.couponCode = params.couponCode;
      payload.couponType = params.couponType;
    }

    return this.http.post(
      `${this.baseUrl}/wallet/razorpay/order/${contentId}`,
      payload,
    );
  }

  /**
   * Opens Razorpay checkout modal and handles payment.
   * Lazily loads the Razorpay SDK script if not already loaded.
   */
  async openCheckout(
    orderData: RazorpayOrderResponse,
    _returnUrl: string,
    page: string,
    onSuccess: (result: RazorpayPaymentResult) => void,
    onDismiss?: () => void,
  ): Promise<void> {
    const loaded = await this.loadScript();
    if (!loaded) {
      this.toaster.error('Unable to load payment gateway. Please try again.');
      return;
    }

    this.analytics.customAnalytics('Redirecting to Payment Gateway', {
      eventType: EventType.log,
      page,
      gateway: 'Razorpay',
    });

    const options = {
      key: orderData.razorpayKeyId,
      amount: orderData.amount,
      currency: orderData.currency,
      name: AppConstant.BRAND,
      description: 'Payment',
      order_id: orderData.razorpayOrderId,
      prefill: {
        ...(orderData.customerName && { name: orderData.customerName }),
        ...(orderData.customerEmail && { email: orderData.customerEmail }),
        ...(orderData.customerPhone && {
          contact: orderData.customerPhone.startsWith('+')
            ? orderData.customerPhone
            : `+${orderData.customerPhone}`,
        }),
      },
      handler: (response: RazorpayPaymentResult) => {
        this.ngZone.run(() => {
          this.analytics.customAnalytics('Transaction Capture Success', {
            razorpayOrderId: orderData.razorpayOrderId,
            razorpayPaymentId: response.razorpay_payment_id,
            amount: orderData.amountInRupees,
            page,
            eventType: EventType.api,
          });
          onSuccess(response);
          this.unloadScript();
        });
      },
      modal: {
        ondismiss: () => {
          this.ngZone.run(() => {
            this.analytics.customAnalytics('Payment Modal Dismissed', {
              razorpayOrderId: orderData.razorpayOrderId,
              page,
              eventType: EventType.log,
            });
            if (onDismiss) onDismiss();
            this.unloadScript();
          });
        },
      },
      theme: {
        color: '#14a38d',
      },
    };

    try {
      const rzp = new Razorpay(options);
      rzp.on('payment.failed', (response: any) => {
        this.ngZone.run(() => {
          this.analytics.failureEvent('Transaction Capture Failure', {
            razorpayOrderId: orderData.razorpayOrderId,
            amount: orderData.amountInRupees,
            page,
            error: 'Razorpay Payment Failed',
            reason: response.error?.description ?? 'Payment failed',
            eventType: EventType.error,
          });
          this.toaster.error(
            response.error?.description ?? 'Payment failed. Please try again.',
          );
          this.unloadScript();
        });
      });
      rzp.open();
    } catch (error: unknown) {
      const reason =
        error instanceof Error
          ? error.message
          : 'Unable to load Razorpay checkout';
      this.analytics.failureEvent('Razorpay SDK Error', {
        page,
        error: 'Razorpay SDK initialization failed',
        reason,
        eventType: EventType.error,
      });
      this.toaster.error('Unable to load payment gateway. Please try again.');
      this.unloadScript();
    }
  }

  /**
   * Get payment/order details (same API as Cashfree)
   */
  getPaymentDetails(orderId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/wallet/orderDetails/${orderId}`);
  }
}
